import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';

export default function AIAutomationPage() {
  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-blue-50 via-purple-50 to-white pt-32 pb-20">
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-full blur-3xl"></div>
            <div className="absolute bottom-20 right-10 w-96 h-96 bg-gradient-to-br from-purple-400/20 to-blue-400/20 rounded-full blur-3xl"></div>
          </div>
          
          <div className="max-w-7xl mx-auto px-6 relative z-10">
            <div className="max-w-3xl">
              <div className="inline-block px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white text-xs font-semibold rounded-full mb-6">
                AI SOLUTIONS
              </div>
              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                AI Automation
                <span className="block bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Solutions
                </span>
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed mb-8">
                Streamline operations and boost productivity with intelligent automation. Let AI handle repetitive tasks while your team focuses on strategic initiatives.
              </p>
              <div className="flex flex-wrap gap-4">
                <a href="/contact" className="px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold hover:shadow-xl transition-all duration-300 whitespace-nowrap cursor-pointer">
                  Automate Now
                </a>
                <a href="/case-studies" className="px-8 py-4 bg-white border-2 border-gray-200 text-gray-700 rounded-lg font-semibold hover:border-purple-500 transition-all duration-300 whitespace-nowrap cursor-pointer">
                  Success Stories
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Key Features */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <div className="inline-block px-4 py-2 bg-gradient-to-r from-blue-100 to-purple-100 text-transparent bg-clip-text text-sm font-semibold rounded-full mb-4">
                AUTOMATION CAPABILITIES
              </div>
              <h2 className="text-4xl font-bold mb-4">What We Automate</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                End-to-end automation solutions for every aspect of your business
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  icon: 'ri-robot-line',
                  title: 'Process Automation',
                  description: 'Automate repetitive workflows, data entry, and routine tasks with intelligent bots.',
                  gradient: 'from-blue-500 to-cyan-500'
                },
                {
                  icon: 'ri-customer-service-2-line',
                  title: 'Customer Service',
                  description: 'AI-powered chatbots and virtual assistants for 24/7 customer support.',
                  gradient: 'from-purple-500 to-pink-500'
                },
                {
                  icon: 'ri-file-text-line',
                  title: 'Document Processing',
                  description: 'Extract, classify, and process documents automatically with AI.',
                  gradient: 'from-indigo-500 to-blue-500'
                },
                {
                  icon: 'ri-mail-line',
                  title: 'Email Automation',
                  description: 'Smart email routing, response generation, and inbox management.',
                  gradient: 'from-violet-500 to-purple-500'
                },
                {
                  icon: 'ri-calendar-check-line',
                  title: 'Scheduling & Planning',
                  description: 'Intelligent scheduling, resource allocation, and calendar management.',
                  gradient: 'from-blue-500 to-indigo-500'
                },
                {
                  icon: 'ri-bar-chart-box-line',
                  title: 'Reporting & Analytics',
                  description: 'Automated report generation, data analysis, and insight delivery.',
                  gradient: 'from-purple-500 to-violet-500'
                }
              ].map((feature, index) => (
                <div 
                  key={index}
                  className="group bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-2xl hover:-translate-y-2 transition-all duration-300"
                >
                  <div className={`w-14 h-14 flex items-center justify-center bg-gradient-to-br ${feature.gradient} rounded-xl mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <i className={`${feature.icon} text-2xl text-white`}></i>
                  </div>
                  <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <div className="inline-block px-4 py-2 bg-gradient-to-r from-blue-100 to-purple-100 text-transparent bg-clip-text text-sm font-semibold rounded-full mb-4">
                BENEFITS
              </div>
              <h2 className="text-4xl font-bold mb-4">Why Automate with AI?</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Transform your business operations with measurable results
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                {
                  icon: 'ri-time-line',
                  stat: '70%',
                  label: 'Time Saved',
                  description: 'Reduce manual work significantly'
                },
                {
                  icon: 'ri-money-dollar-circle-line',
                  stat: '50%',
                  label: 'Cost Reduction',
                  description: 'Lower operational expenses'
                },
                {
                  icon: 'ri-error-warning-line',
                  stat: '95%',
                  label: 'Error Reduction',
                  description: 'Minimize human errors'
                },
                {
                  icon: 'ri-speed-up-line',
                  stat: '3x',
                  label: 'Faster Processing',
                  description: 'Accelerate task completion'
                }
              ].map((benefit, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-2xl p-8 border border-gray-200 text-center hover:shadow-xl transition-all duration-300"
                >
                  <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-blue-500 to-purple-500 rounded-full mx-auto mb-6">
                    <i className={`${benefit.icon} text-2xl text-white`}></i>
                  </div>
                  <div className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
                    {benefit.stat}
                  </div>
                  <div className="text-lg font-semibold mb-2">{benefit.label}</div>
                  <p className="text-gray-600 text-sm">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Use Cases */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <div className="inline-block px-4 py-2 bg-gradient-to-r from-blue-100 to-purple-100 text-transparent bg-clip-text text-sm font-semibold rounded-full mb-4">
                USE CASES
              </div>
              <h2 className="text-4xl font-bold mb-4">Industry Applications</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                AI automation solutions tailored for different industries
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {[
                {
                  industry: 'Finance',
                  icon: 'ri-bank-line',
                  applications: ['Invoice processing', 'Fraud detection', 'Compliance reporting', 'Account reconciliation']
                },
                {
                  industry: 'Healthcare',
                  icon: 'ri-hospital-line',
                  applications: ['Patient scheduling', 'Medical records management', 'Insurance claims', 'Appointment reminders']
                },
                {
                  industry: 'Retail',
                  icon: 'ri-shopping-cart-line',
                  applications: ['Inventory management', 'Order processing', 'Customer support', 'Price optimization']
                },
                {
                  industry: 'Manufacturing',
                  icon: 'ri-factory-line',
                  applications: ['Quality control', 'Supply chain optimization', 'Predictive maintenance', 'Production scheduling']
                }
              ].map((useCase, index) => (
                <div 
                  key={index}
                  className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-8 hover:shadow-xl transition-all duration-300"
                >
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg">
                      <i className={`${useCase.icon} text-xl text-white`}></i>
                    </div>
                    <h3 className="text-2xl font-bold">{useCase.industry}</h3>
                  </div>
                  <ul className="space-y-3">
                    {useCase.applications.map((app, idx) => (
                      <li key={idx} className="flex items-center gap-3">
                        <div className="w-1.5 h-1.5 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"></div>
                        <span className="text-gray-700">{app}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-4xl font-bold mb-6">
              Start Your
              <span className="block bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Automation Journey
              </span>
            </h2>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Discover how AI automation can revolutionize your business operations and drive efficiency.
            </p>
            <a 
              href="/contact"
              className="inline-block px-10 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold hover:shadow-xl transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Get Started Today
            </a>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
}